/**
 * Polyfills for crypto APIs
 */

/**
 * Polyfill for crypto.randomUUID()
 * Provides UUID v4 generation for environments that don't support the native API
 */

// Check if crypto.randomUUID is already available
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
    // Polyfill using crypto.getRandomValues (widely supported)
    crypto.randomUUID = function randomUUID() {
        // Generate random bytes
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);

        // Set version (4) and variant bits according to RFC 4122
        bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
        bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10

        // Convert bytes to UUID string format
        const hex = Array.from(bytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');

        return [
            hex.substring(0, 8),
            hex.substring(8, 12),
            hex.substring(12, 16),
            hex.substring(16, 20),
            hex.substring(20, 32),
        ].join('-') as `${string}-${string}-${string}-${string}-${string}`;
    };
}

/**
 * Polyfill for crypto.subtle.digest()
 * Uses @noble/hashes library (loaded asynchronously) for secure, audited implementations
 */

// Ensure crypto.subtle exists
if (typeof crypto !== 'undefined' && !crypto.subtle) {
    // @ts-expect-error - Adding subtle property to crypto
    crypto.subtle = {};
}

// Check if crypto.subtle.digest is already available
if (typeof crypto !== 'undefined' && crypto.subtle && !crypto.subtle.digest) {
    crypto.subtle.digest = async function digest(
        algorithm: AlgorithmIdentifier,
        data: BufferSource
    ): Promise<ArrayBuffer> {
        const algo = typeof algorithm === 'string' ? algorithm : algorithm.name;
        const normalizedAlgo = algo.toUpperCase().replace(/-/g, '');

        // Convert data to Uint8Array
        const bytes =
            data instanceof ArrayBuffer
                ? new Uint8Array(data)
                : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);

        // Dynamically import @noble/hashes only when needed
        const { sha256, sha384, sha512 } = await import('@noble/hashes/sha2.js');
        let result: Uint8Array;
        switch (normalizedAlgo) {
            case 'SHA256': {
                result = sha256(bytes);
                break;
            }
            case 'SHA384': {
                result = sha384(bytes);
                break;
            }
            case 'SHA512': {
                result = sha512(bytes);
                break;
            }
            case 'SHA1': {
                const { sha1 } = await import('@noble/hashes/legacy.js');
                result = sha1(bytes);
                break;
            }
            default:
                throw new Error(`Unsupported algorithm: ${algo}`);
        }

        // @noble/hashes returns Uint8Array, convert to ArrayBuffer
        const buffer = new ArrayBuffer(result.length);
        const view = new Uint8Array(buffer);
        view.set(result);
        return buffer;
    };
}

export {};
